import generalController from "../controllers/generalController";

// Environment types
type Environment = 'dev' | 'stage' | 'prod';

// Available endpoint names
export type EndpointName =
  | 'contact-dealer'
  | 'kmi'
  | 'request-quote'
  | 'reserve-yours'
  | 'pre-order'
  | 'test-drive'
  | 'contact'
  | 'service-appt'
  | 'geolocation'
  | 'cbb'
  | 'model-years'
  | 'model-trims'
  | 'model-accessories'
  | 'model-search'
  | 'find-dealers'
  | 'get-dealer'
  | 'save-build'
  | 'share-build'
  | 'get-specs'
  | 'legend-nomination';

// Endpoint configuration type
type EndpointConfig = Record<EndpointName, string>;

/**
 * Return the API Base URL according to which environment we are on.
 */
export function getApiBaseUrl(): string {
  if (window.location.host.indexOf("localhost") !== -1)
      return process.env.REACT_APP_MAZDA_DRUPAL_API_DEV || '';
  else if (window.location.host.indexOf("dev") !== -1)
      return process.env.REACT_APP_MAZDA_DRUPAL_API_DEV || '';
  else if (window.location.host.indexOf("preprod") !== -1)
      return process.env.REACT_APP_MAZDA_DRUPAL_API_PREPROD || '';
  else
      return process.env.REACT_APP_MAZDA_DRUPAL_API_PROD || '';
}

/**
 * Return endpoint URL for the given endpoint name.
 */
export function getEndpointUrl(name: EndpointName): string | null {
  const langCode = generalController.detectLanguage();
  const province = generalController.getMetatagValue('province');
  // Get a mazda drupal API url.
  const mzDrupalAPI = getApiBaseUrl();

  let env: Environment = 'dev';

  // Get the current environment (either "dev", "stage", or "prod")
  if (window.location.host.indexOf("localhost") !== -1)
      env = 'dev';
  else if (window.location.host.indexOf("dev") !== -1)
      env = 'dev';
  else if (window.location.host.indexOf("preprod") !== -1)
      env = 'stage'; 
  else
      env = 'prod';

  const endpointsList: Record<Environment, EndpointConfig> = {
    dev: {
      // CPO APIs
      'contact-dealer': 'https://devcpo.mazda.ca/api/contactdealerservice/submit',
      'kmi': 'https://devcpo.mazda.ca/API/keepmeinformed/submit',
      'request-quote': 'https://devcpo.mazda.ca/API/requestaquote/submit',
      'reserve-yours':'https://devcpo.mazda.ca/API/requestaquote/submit',
      'pre-order':'https://devcpo.mazda.ca/API/contactdealerservice/submit',
      'test-drive': 'https://devcpo.mazda.ca/API/bookatestdrive/submit',
      'contact': "https://devcpo.mazda.ca/API/contactus/submit",
      'service-appt': "https://devcpo.mazda.ca/API/BookService/Submit",
      'geolocation': "https://devcpo.mazda.ca/api/geocode/json",
      'cbb': `https://devcpo.mazda.ca/API/CanBlackBook/submit`,
      // Mazda Drupal API
      'model-years': `${mzDrupalAPI}/api/ModelYears?lang_code=${langCode}&prov_code=${province}`,
      'model-trims': `${mzDrupalAPI}/api/Trims`,
      'model-accessories': `${mzDrupalAPI}/api/Accessories`,
      'model-search': `${mzDrupalAPI}/api/Model`,
      'find-dealers': `${mzDrupalAPI}/api/Dealers?lang_code=${langCode}&limit=50`,
      'get-dealer': `${mzDrupalAPI}/api/Dealers`,
      'save-build': `${mzDrupalAPI}/api/BuildYourMazda/save`,
      'share-build': `${mzDrupalAPI}/api/BuildYourMazda/share`,
      'get-specs': `${mzDrupalAPI}/api/specs?lang_code=${langCode}`,
      // Forms Nomination
      'legend-nomination': `${mzDrupalAPI}/LegendNomination?lang_code=${langCode}`,
    },
    stage: {
      // CPO APIs
      // stage has been removed because it's not used by anyone, we skip process at WT
      'contact-dealer': 'https://devcpo.mazda.ca/api/contactdealerservice/submit',
      'kmi': 'https://devcpo.mazda.ca/API/keepmeinformed/submit',
      'request-quote': 'https://devcpo.mazda.ca/API/requestaquote/submit',
      'reserve-yours':'https://devcpo.mazda.ca/API/requestaquote/submit',
      'pre-order':'https://devcpo.mazda.ca/API/contactdealerservice/submit',
      'test-drive': 'https://devcpo.mazda.ca/API/bookatestdrive/submit',
      'contact': "https://devcpo.mazda.ca/API/contactus/submit",
      'service-appt': "https://devcpo.mazda.ca/API/BookService/Submit",
      'geolocation': "https://devcpo.mazda.ca/api/geocode/json",
      'cbb': `https://devcpo.mazda.ca/API/CanBlackBook/submit`,      
      // Mazda Drupal API
      'model-years': `${mzDrupalAPI}/api/ModelYears?lang_code=${langCode}&prov_code=${province}`,
      'model-trims': `${mzDrupalAPI}/api/Trims`,
      'model-accessories': `${mzDrupalAPI}/api/Accessories`,
      'model-search': `${mzDrupalAPI}/api/Model`,
      'find-dealers': `${mzDrupalAPI}/api/Dealers?lang_code=${langCode}&limit=50`,
      'get-dealer': `${mzDrupalAPI}/api/Dealers`,
      'save-build': `${mzDrupalAPI}/api/BuildYourMazda/save`,
      'share-build': `${mzDrupalAPI}/api/BuildYourMazda/share`,
      'get-specs': `${mzDrupalAPI}/api/specs?lang_code=${langCode}`,
      // Forms Nomination
      'legend-nomination': `${mzDrupalAPI}/LegendNomination?lang_code=${langCode}`,
    },
    prod: {
      // CPO APIs
      'contact-dealer': 'https://cpo.mazda.ca/api/contactdealerservice/submit',
      'kmi': 'https://cpo.mazda.ca/API/keepmeinformed/submit',
      'request-quote': 'https://cpo.mazda.ca/API/requestaquote/submit',
      'reserve-yours': 'https://cpo.mazda.ca/API/requestaquote/submit',
      'pre-order': 'https://cpo.mazda.ca/API/contactdealerservice/submit',
      'test-drive': 'https://cpo.mazda.ca/API/bookatestdrive/submit',
      'contact': "https://cpo.mazda.ca/API/contactus/submit",
      'service-appt': "https://cpo.mazda.ca/API/BookService/Submit",
      'geolocation': "https://cpo.mazda.ca/api/geocode/json",
      'cbb': `https://cpo.mazda.ca/API/CanBlackBook/submit`,
      // Mazda Drupal API
      'model-years': `${mzDrupalAPI}/api/ModelYears?lang_code=${langCode}&prov_code=${province}`,
      'model-trims': `${mzDrupalAPI}/api/Trims`,
      'model-accessories': `${mzDrupalAPI}/api/Accessories`,
      'model-search': `${mzDrupalAPI}/api/Model`,
      'find-dealers': `${mzDrupalAPI}/api/Dealers?lang_code=${langCode}&limit=50`,
      'get-dealer': `${mzDrupalAPI}/api/Dealers`,
      'save-build': `${mzDrupalAPI}/api/BuildYourMazda/save`,
      'share-build': `${mzDrupalAPI}/api/BuildYourMazda/share`,
      'get-specs': `${mzDrupalAPI}/api/specs?lang_code=${langCode}`,
      // Forms Nomination
      'legend-nomination': `${mzDrupalAPI}/LegendNomination?lang_code=${langCode}`,
    }
  };

  if (endpointsList[env] && endpointsList[env][name]) {
    return endpointsList[env][name];
  } else {
    console.log('Error: End-point not found in endPoints.js');
    return null;
  }
}
